import CreateContentModal from '@/components/CreateContentModal';
import { useProfKylianContent } from '@/hooks/useProfKylianContent';
import { ContentItem, useRevisionContent } from '@/hooks/useRevisionContent';
import { useUserProfile } from '@/hooks/useUserProfile';
import { getMonthStartUTC } from '@/lib/dates';
import { supabase } from '@/lib/supabase';
import { LinearGradient } from 'expo-linear-gradient';
import { router, useFocusEffect } from 'expo-router';
import {
  BookOpenText,
  Cards,
  CaretRight,
  ChatCircleDots,
  Check,
  FileText,
  FolderOpen,
  Gift,
  GraduationCap,
  Lock,
  PencilSimple,
  Question,
  Trash
} from 'phosphor-react-native';
import { useCallback, useEffect, useState } from 'react';
import { Alert, Modal, Platform, ScrollView, StyleSheet, TextInput, TouchableOpacity, View } from 'react-native';
import { ActivityIndicator, Text } from 'react-native-paper';

// ✅ Détection plateforme web
const isWeb = Platform.OS === 'web';
const MAX_WIDTH = 1200;

// Design System Colors
const colors = {
  indigo700: '#4F46E5',
  indigo600: '#6366F1',
  indigo500: '#818CF8',
  indigo200: '#C7D2FE',
  indigo100: '#E0E7FF',
  indigo50: '#EEF2FF',
  slate900: '#0F172A',
  slate800: '#1E293B',
  slate700: '#334155',
  slate600: '#475569',
  slate500: '#64748B',
  slate400: '#94A3B8',
  slate300: '#CBD5E1',
  slate200: '#E2E8F0',
  slate100: '#F1F5F9',
  slate50: '#F8FAFC',
  white: '#FFFFFF',
  amber500: '#F59E0B',
  amber400: '#FBBF24',
  amber100: '#FEF3C7',
  orange500: '#F97316',
  emerald600: '#059669',
  emerald500: '#10B981',
  emerald100: '#D1FAE5',
  rose500: '#F43F5E',
  rose100: '#FFE4E6',
  purple600: '#7C3AED',
  purple100: '#EDE9FE',
};

// ✅ Limites FREE (par mois) - METS À 0 POUR TESTER
export const FREE_LIMITS = {
  fiches: 5,
  quiz: 5,
  flashcards: 5,
  coach: 10,
};

// ✅ XP par action - exportés pour le modal
export const XP_VALUES = {
  fiche: 10,
  quiz: 20,
  flashcards: 10,
  coach: 5,
};

export default function ReviseScreen() {
  const { profile, loading: profileLoading } = useUserProfile();
  const { loading, getFilteredItems, refresh } = useRevisionContent();
  const [activeFilter, setActiveFilter] = useState<'all' | 'fiches' | 'quiz' | 'flashcards'>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createIntent, setCreateIntent] = useState<'fiche' | 'quiz' | 'flashcards'>('fiche');
  
  const [selectedItem, setSelectedItem] = useState<ContentItem | null>(null);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // ✅ Compteurs mensuels pour les quotas
  const [monthlyUsage, setMonthlyUsage] = useState({
    fiches: 0,
    quiz: 0,
    flashcards: 0,
    coach: 0,
  });

  const items = getFilteredItems(activeFilter);
  const isPremium = profile?.premium === true;

  // ✅ Hook Prof Kylian pour les stats dynamiques
  const { stats: profKylianStats } = useProfKylianContent();
  const totalFreeContent = profKylianStats.freeFiches + profKylianStats.freeVideos + profKylianStats.freeQuiz + profKylianStats.freeExamens;

  // ✅ Récupérer les compteurs mensuels
  const fetchMonthlyUsage = useCallback(async () => {
    // Attendre que profile soit chargé
    if (!profile?.id) {
      console.log('[Revise] fetchMonthlyUsage: profile not ready');
      return;
    }
    
    console.log('[Revise] fetchMonthlyUsage: fetching for user', profile.id);
    
    try {
      const userId = profile.id;
      const monthStart = getMonthStartUTC();

      // Compter les créations ce mois-ci en parallèle
      const [notesRes, quizzesRes, flashcardsRes, coachRes] = await Promise.all([
        supabase
          .from('notes')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', userId)
          .gte('created_at', monthStart),
        supabase
          .from('quizzes')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', userId)
          .gte('created_at', monthStart),
        supabase
          .from('flashcard_sets')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', userId)
          .gte('created_at', monthStart),
        supabase
          .from('chat_messages')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', userId)
          .eq('role', 'user')
          .gte('created_at', monthStart),
      ]);

      const newUsage = {
        fiches: notesRes.count ?? 0,
        quiz: quizzesRes.count ?? 0,
        flashcards: flashcardsRes.count ?? 0,
        coach: coachRes.count ?? 0,
      };
      
      console.log('[Revise] monthlyUsage updated:', newUsage);
      console.log('[Revise] FREE_LIMITS:', FREE_LIMITS);
      
      setMonthlyUsage(newUsage);
    } catch (error) {
      console.error('Error fetching monthly usage:', error);
    }
  }, [profile?.id]);

  // ✅ Charger les compteurs quand profile devient disponible
  useEffect(() => {
    if (profile?.id) {
      console.log('[Revise] profile ready, fetching usage');
      fetchMonthlyUsage();
    }
  }, [profile?.id, fetchMonthlyUsage]);

  // ✅ Rafraîchir les compteurs quand l'écran devient actif
  useFocusEffect(
    useCallback(() => {
      if (profile?.id) {
        fetchMonthlyUsage();
      }
    }, [profile?.id, fetchMonthlyUsage])
  );

  // ✅ Vérifier si la limite est atteinte
  const isLimitReached = (type: 'fiches' | 'quiz' | 'flashcards' | 'coach') => {
    if (isPremium) return false;
    const usage = type === 'fiches' ? monthlyUsage.fiches :
                  type === 'quiz' ? monthlyUsage.quiz :
                  type === 'flashcards' ? monthlyUsage.flashcards :
                  monthlyUsage.coach;
    const limit = FREE_LIMITS[type];
    const reached = usage >= limit;
    // Debug log
    console.log(`[Revise] isLimitReached(${type}): usage=${usage}, limit=${limit}, reached=${reached}`);
    return reached;
  };

  const getRelativeTime = (dateString: string | null) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return "aujourd'hui";
    if (diffInDays === 1) return 'hier';
    if (diffInDays < 7) return `il y a ${diffInDays} jours`;
    if (diffInDays < 30) return `il y a ${Math.floor(diffInDays / 7)} sem.`;
    return `il y a ${Math.floor(diffInDays / 30)} mois`;
  };

  const getTypeIcon = (type: ContentItem['type'], size: number = 24) => {
    switch (type) {
      case 'fiche': return <FileText size={size} color={colors.indigo600} weight="duotone" />;
      case 'quiz': return <Question size={size} color={colors.amber500} weight="duotone" />;
      case 'flashcards': return <Cards size={size} color={colors.emerald500} weight="duotone" />;
    }
  };

  const getTypeLabel = (type: ContentItem['type']) => {
    switch (type) {
      case 'fiche': return 'Fiche';
      case 'quiz': return 'Quiz';
      case 'flashcards': return 'Flashcards';
    }
  };

  const getTypeColor = (type: ContentItem['type']) => {
    switch (type) {
      case 'fiche': return colors.indigo600;
      case 'quiz': return colors.amber500;
      case 'flashcards': return colors.emerald500;
    }
  };

  const getTypeBgColor = (type: ContentItem['type']) => {
    switch (type) {
      case 'fiche': return colors.indigo100;
      case 'quiz': return colors.amber100;
      case 'flashcards': return colors.emerald100;
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 16) return colors.emerald500;
    if (score >= 10) return colors.amber500;
    return colors.rose500;
  };

  const getScoreBgColor = (score: number) => {
    if (score >= 16) return colors.emerald100;
    if (score >= 10) return colors.amber100;
    return colors.rose100;
  };

  const getTableName = (type: ContentItem['type']) => {
    switch (type) {
      case 'fiche': return 'notes';
      case 'quiz': return 'quizzes';
      case 'flashcards': return 'flashcard_sets';
    }
  };

  // ✅ Fonction pour rediriger vers le paywall
  const redirectToPaywall = () => {
    router.push('/paywall');
  };

  // ✅ Afficher l'Alert paywall - BLOCAGE IMMÉDIAT avec "Passer Premium" EN GRAS
  const showPaywallAlert = (contentType: string) => {
    const limit = contentType === 'coach' ? FREE_LIMITS.coach :
                  contentType === 'fiche' ? FREE_LIMITS.fiches :
                  contentType === 'quiz' ? FREE_LIMITS.quiz :
                  FREE_LIMITS.flashcards;
    
    const contentName = contentType === 'coach' ? 'messages Coach IA' :
                        contentType === 'fiche' ? 'fiches' :
                        contentType === 'quiz' ? 'quiz' : 'sets de flashcards';

    const message = `Tu as atteint ta limite de ${limit} ${contentName} par mois. Passe Premium pour un accès illimité !`;

    if (isWeb) {
      if (window.confirm(message + '\n\nPasser Premium ?')) {
        redirectToPaywall();
      }
    } else {
      Alert.alert(
        'Limite atteinte',
        message,
        [
          { 
            text: 'Passer Premium', 
            style: 'default',
            isPreferred: true,  // ← BOUTON EN GRAS sur iOS
            onPress: redirectToPaywall 
          },
          { text: 'Plus tard', style: 'cancel' },
        ]
      );
    }
  };

  // ✅ BLOCAGE IMMÉDIAT AU CLIC si limite atteinte
  const handleCreateAction = (action: string) => {
    console.log(`[Revise] handleCreateAction: ${action}`);
    
    if (action === 'question') {
      if (isLimitReached('coach')) {
        showPaywallAlert('coach');
        return;
      }
      router.push('/(tabs)/coach');
    } else if (action === 'fiche') {
      if (isLimitReached('fiches')) {
        showPaywallAlert('fiche');
        return;
      }
      setCreateIntent(action);
      setShowCreateModal(true);
    } else if (action === 'quiz') {
      if (isLimitReached('quiz')) {
        showPaywallAlert('quiz');
        return;
      }
      setCreateIntent(action);
      setShowCreateModal(true);
    } else if (action === 'flashcards') {
      if (isLimitReached('flashcards')) {
        showPaywallAlert('flashcards');
        return;
      }
      setCreateIntent(action);
      setShowCreateModal(true);
    }
  };

  const handleModalClose = () => {
    setShowCreateModal(false);
    refresh();
    fetchMonthlyUsage();
  };

  const handleItemPress = (item: ContentItem) => {
    switch (item.type) {
      case 'fiche': router.push(`/notes/${item.id}`); break;
      case 'quiz': router.push(`/quiz/${item.id}`); break;
      case 'flashcards': router.push(`/flashcards/${item.id}`); break;
    }
  };

  const handleItemLongPress = (item: ContentItem) => {
    setSelectedItem(item);
    setShowActionsMenu(true);
  };

  const handleMenuOption = (option: 'rename' | 'delete') => {
    setShowActionsMenu(false);
    
    if (option === 'rename' && selectedItem) {
      setNewTitle(selectedItem.title || '');
      setShowRenameModal(true);
    } else if (option === 'delete' && selectedItem) {
      if (isWeb) {
        if (window.confirm(`Supprimer "${selectedItem.title || 'Sans titre'}" ?`)) {
          handleDelete();
        }
      } else {
        Alert.alert(
          'Supprimer',
          `Supprimer "${selectedItem.title || 'Sans titre'}" ?`,
          [
            { text: 'Annuler', style: 'cancel' },
            { text: 'Supprimer', style: 'destructive', onPress: handleDelete },
          ]
        );
      }
    }
  };

  const handleRename = async () => {
    if (!selectedItem || !newTitle.trim()) return;
    
    setIsSaving(true);
    try {
      const tableName = getTableName(selectedItem.type);
      const { error } = await supabase
        .from(tableName)
        .update({ title: newTitle.trim() })
        .eq('id', selectedItem.id);

      if (error) throw error;

      setShowRenameModal(false);
      setSelectedItem(null);
      setNewTitle('');
      refresh();
    } catch (error) {
      console.error('Error renaming:', error);
      if (isWeb) {
        window.alert('Impossible de renommer');
      } else {
        Alert.alert('Erreur', 'Impossible de renommer');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedItem) return;
    
    try {
      const tableName = getTableName(selectedItem.type);
      const { error } = await supabase
        .from(tableName)
        .delete()
        .eq('id', selectedItem.id);

      if (error) throw error;
      setSelectedItem(null);
      refresh();
      fetchMonthlyUsage();
    } catch (error) {
      console.error('Error deleting:', error);
      if (isWeb) {
        window.alert('Impossible de supprimer');
      } else {
        Alert.alert('Erreur', 'Impossible de supprimer');
      }
    }
  };

  const handleProfKylianPress = () => {
    router.push('/prof-kylian');
  };

  const filters = [
    { key: 'all', label: 'Tout' },
    { key: 'fiches', label: 'Fiches' },
    { key: 'quiz', label: 'Quiz' },
    { key: 'flashcards', label: 'Flashcards' },
  ];

  // ✅ Attendre que profile ET content soient chargés
  if (loading || profileLoading) {
    return (
      <View style={[styles.container, styles.centered]}>
        <ActivityIndicator size="large" color={colors.indigo600} />
        <Text style={styles.loadingText}>Chargement...</Text>
      </View>
    );
  }

  // ✅ Calculer les limites une fois pour le rendu (évite re-calcul multiple)
  const coachLocked = isLimitReached('coach');
  const fichesLocked = isLimitReached('fiches');
  const quizLocked = isLimitReached('quiz');
  const flashcardsLocked = isLimitReached('flashcards');

  return (
    <>
      <View style={styles.container}>
        {/* ✅ Header FIXE avec gradient violet - centré sur web */}
        <View style={styles.headerWrapper}>
          <LinearGradient
            colors={[colors.indigo500, colors.indigo600, colors.indigo700]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={styles.headerFixed}
          >
            <View style={styles.headerInner}>
              <View style={styles.headerIconCircle}>
                <BookOpenText size={28} color={colors.indigo600} weight="duotone" />
              </View>
              <View style={styles.headerTextContainer}>
                <Text style={styles.headerTitle}>Réviser</Text>
                <Text style={styles.headerSubtitle}>{items.length} contenu{items.length > 1 ? 's' : ''}</Text>
              </View>
            </View>
          </LinearGradient>
        </View>

        <ScrollView 
          style={styles.scrollView} 
          contentContainerStyle={styles.contentContainer}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.innerContainer}>

            {/* ━━ SECTION 1: Créer un contenu ━━ */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Créer un contenu</Text>
              
              <View style={styles.actionsGrid}>
                <View style={styles.actionsRow}>
                  <TouchableOpacity 
                    style={styles.actionCard} 
                    activeOpacity={0.8}
                    onPress={() => handleCreateAction('question')}
                  >
                    <LinearGradient
                      colors={[colors.indigo100, colors.indigo50]}
                      style={styles.actionIconGradient}
                    >
                      <ChatCircleDots size={28} color={colors.indigo600} weight="duotone" />
                    </LinearGradient>
                    <Text style={styles.actionTitle}>Coach IA</Text>
                    <Text style={styles.actionSubtitle}>Poser une question</Text>
                    {/* ✅ Cadenas si limite atteinte */}
                    {coachLocked && (
                      <View style={styles.actionLocked}>
                        <Lock size={16} color={colors.white} weight="fill" />
                      </View>
                    )}
                  </TouchableOpacity>

                  <TouchableOpacity 
                    style={styles.actionCard} 
                    activeOpacity={0.8}
                    onPress={() => handleCreateAction('fiche')}
                  >
                    <LinearGradient
                      colors={[colors.indigo100, colors.indigo50]}
                      style={styles.actionIconGradient}
                    >
                      <FileText size={28} color={colors.indigo600} weight="duotone" />
                    </LinearGradient>
                    <Text style={styles.actionTitle}>Fiche</Text>
                    <Text style={styles.actionSubtitle}>Depuis ton cours</Text>
                    {/* ✅ Cadenas si limite atteinte */}
                    {fichesLocked && (
                      <View style={styles.actionLocked}>
                        <Lock size={16} color={colors.white} weight="fill" />
                      </View>
                    )}
                  </TouchableOpacity>
                </View>

                <View style={styles.actionsRow}>
                  <TouchableOpacity 
                    style={styles.actionCard} 
                    activeOpacity={0.8}
                    onPress={() => handleCreateAction('quiz')}
                  >
                    <LinearGradient
                      colors={[colors.amber100, '#FFFBEB']}
                      style={styles.actionIconGradient}
                    >
                      <Question size={28} color={colors.amber500} weight="duotone" />
                    </LinearGradient>
                    <Text style={styles.actionTitle}>Quiz</Text>
                    <Text style={styles.actionSubtitle}>Depuis ton cours</Text>
                    {/* ✅ Cadenas si limite atteinte */}
                    {quizLocked && (
                      <View style={styles.actionLocked}>
                        <Lock size={16} color={colors.white} weight="fill" />
                      </View>
                    )}
                  </TouchableOpacity>

                  <TouchableOpacity 
                    style={styles.actionCard} 
                    activeOpacity={0.8}
                    onPress={() => handleCreateAction('flashcards')}
                  >
                    <LinearGradient
                      colors={[colors.emerald100, '#ECFDF5']}
                      style={styles.actionIconGradient}
                    >
                      <Cards size={28} color={colors.emerald500} weight="duotone" />
                    </LinearGradient>
                    <Text style={styles.actionTitle}>Flashcards</Text>
                    <Text style={styles.actionSubtitle}>Depuis ton cours</Text>
                    {/* ✅ Cadenas si limite atteinte */}
                    {flashcardsLocked && (
                      <View style={styles.actionLocked}>
                        <Lock size={16} color={colors.white} weight="fill" />
                      </View>
                    )}
                  </TouchableOpacity>
                </View>
              </View>
            </View>

            {/* ━━ SECTION 2: Contenu Prof Kylian ━━ */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Contenu du prof</Text>
              
              <TouchableOpacity
                activeOpacity={0.9}
                onPress={handleProfKylianPress}
              >
                <LinearGradient
                  colors={[colors.indigo100, colors.purple100]}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  style={styles.profKylianCard}
                >
                  <View style={styles.profKylianHeader}>
                    <View style={styles.profKylianIconContainer}>
                      <GraduationCap size={28} color={colors.indigo600} weight="duotone" />
                    </View>
                    <View style={styles.profKylianTitleContainer}>
                      <Text style={styles.profKylianTitle}>Fiches, vidéos, quiz{'\n'}+ sujets corrigés</Text>
                      <Text style={styles.profKylianSubtitle}>Créé par Kylian (prof de BTS)</Text>
                    </View>
                  </View>

                  <View style={styles.profKylianPoints}>
                    <View style={styles.profKylianPointRow}>
                      <Check size={18} color={colors.emerald500} weight="bold" />
                      <Text style={styles.profKylianPointText}>100% conformes aux programmes officiels</Text>
                    </View>
                    <View style={styles.profKylianPointRow}>
                      <Check size={18} color={colors.emerald500} weight="bold" />
                      <Text style={styles.profKylianPointText}>+25 000 étudiants satisfaits</Text>
                    </View>
                    <View style={styles.profKylianPointRow}>
                      <Check size={18} color={colors.emerald500} weight="bold" />
                      <Text style={styles.profKylianPointText}>+3 points en moyenne</Text>
                    </View>
                  </View>

                  <View style={styles.profKylianMatieres}>
                    <View style={styles.profKylianMatiereBadge}>
                      <Text style={styles.profKylianMatiereText}>CEJM</Text>
                    </View>
                    <View style={styles.profKylianMatiereBadge}>
                      <Text style={styles.profKylianMatiereText}>Gestion</Text>
                    </View>
                  </View>

                  {!isPremium && totalFreeContent > 0 && (
                    <View style={styles.profKylianFreeRow}>
                      <Gift size={18} color={colors.emerald500} weight="fill" />
                      <Text style={styles.profKylianFreeText}>
                        {totalFreeContent} contenu{totalFreeContent > 1 ? 's' : ''} gratuit{totalFreeContent > 1 ? 's' : ''}
                      </Text>
                    </View>
                  )}

                  <View style={styles.profKylianCTA}>
                    <Text style={styles.profKylianCTAText}>Découvrir le contenu</Text>
                  </View>
                </LinearGradient>
              </TouchableOpacity>
            </View>

            {/* ━━ SECTION 3: Mes contenus ━━ */}
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionTitle}>Mes contenus</Text>
                <Text style={styles.sectionHint}>Appui long pour modifier</Text>
              </View>

              <View style={styles.filtersContainer}>
                {filters.map((filter) => (
                  <TouchableOpacity
                    key={filter.key}
                    style={[
                      styles.filterChip,
                      activeFilter === filter.key && styles.filterChipActive,
                    ]}
                    onPress={() => setActiveFilter(filter.key as typeof activeFilter)}
                    activeOpacity={0.7}
                  >
                    <Text style={[
                      styles.filterChipText,
                      activeFilter === filter.key && styles.filterChipTextActive,
                    ]}>
                      {filter.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>

              {items.length === 0 ? (
                <View style={styles.emptyCard}>
                  <FolderOpen size={48} color={colors.slate400} weight="duotone" />
                  <Text style={styles.emptyTitle}>Aucun contenu</Text>
                  <Text style={styles.emptySubtitle}>
                    Crée ta première fiche ou ton premier quiz pour commencer !
                  </Text>
                </View>
              ) : (
                <View style={styles.contentList}>
                  {items.map((item) => (
                    <TouchableOpacity
                      key={`${item.type}-${item.id}`}
                      activeOpacity={0.8}
                      onPress={() => handleItemPress(item)}
                      onLongPress={() => handleItemLongPress(item)}
                      delayLongPress={500}
                    >
                      <View style={styles.contentCard}>
                        <LinearGradient
                          colors={[getTypeBgColor(item.type), colors.white]}
                          style={styles.contentIconCircle}
                        >
                          {getTypeIcon(item.type, 22)}
                        </LinearGradient>
                        <View style={styles.contentInfo}>
                          <Text style={styles.contentTitle} numberOfLines={1}>
                            {item.title || 'Sans titre'}
                          </Text>
                          <View style={styles.contentMeta}>
                            <View style={[styles.typeBadge, { backgroundColor: getTypeBgColor(item.type) }]}>
                              <Text style={[styles.typeBadgeText, { color: getTypeColor(item.type) }]}>
                                {getTypeLabel(item.type)}
                              </Text>
                            </View>
                            <Text style={styles.contentDate}>
                              {getRelativeTime(item.created_at)}
                            </Text>
                          </View>
                        </View>

                        {item.type === 'quiz' && item.lastScore !== null && item.lastScore !== undefined && (
                          <View style={[
                            styles.scoreBadge, 
                            { backgroundColor: getScoreBgColor(item.lastScore) }
                          ]}>
                            <Text style={[
                              styles.scoreBadgeText,
                              { color: getScoreColor(item.lastScore) }
                            ]}>
                              {item.lastScore}/20
                            </Text>
                          </View>
                        )}

                        <View style={styles.contentChevron}>
                          <CaretRight size={20} color={colors.slate400} weight="bold" />
                        </View>
                      </View>
                    </TouchableOpacity>
                  ))}
                </View>
              )}
            </View>

            <View style={{ height: 120 }} />
          </View>
        </ScrollView>
      </View>

      <CreateContentModal
        visible={showCreateModal}
        onClose={handleModalClose}
        initialIntent={createIntent}
        monthlyUsage={monthlyUsage}
        isPremium={isPremium}
      />

      <Modal
        visible={showActionsMenu}
        transparent
        animationType="fade"
        onRequestClose={() => setShowActionsMenu(false)}
      >
        <TouchableOpacity 
          style={styles.menuOverlay}
          activeOpacity={1}
          onPress={() => setShowActionsMenu(false)}
        >
          <View style={styles.menuContainer}>
            <View style={styles.menuHandle} />
            <Text style={styles.menuTitle} numberOfLines={1}>
              {selectedItem?.title || 'Sans titre'}
            </Text>
            
            <TouchableOpacity 
              style={styles.menuOption}
              onPress={() => handleMenuOption('rename')}
              activeOpacity={0.7}
            >
              <View style={styles.menuOptionIcon}>
                <PencilSimple size={22} color={colors.indigo600} weight="duotone" />
              </View>
              <Text style={styles.menuOptionText}>Renommer</Text>
              <CaretRight size={18} color={colors.slate400} weight="bold" />
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={styles.menuOption}
              onPress={() => handleMenuOption('delete')}
              activeOpacity={0.7}
            >
              <View style={[styles.menuOptionIcon, { backgroundColor: colors.rose500 + '15' }]}>
                <Trash size={22} color={colors.rose500} weight="duotone" />
              </View>
              <Text style={[styles.menuOptionText, styles.menuOptionDelete]}>Supprimer</Text>
              <CaretRight size={18} color={colors.slate400} weight="bold" />
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={styles.menuCancel}
              onPress={() => setShowActionsMenu(false)}
              activeOpacity={0.7}
            >
              <Text style={styles.menuCancelText}>Annuler</Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>

      <Modal
        visible={showRenameModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowRenameModal(false)}
      >
        <View style={styles.renameOverlay}>
          <View style={styles.renameContainer}>
            <Text style={styles.renameTitle}>Renommer</Text>
            
            <TextInput
              style={styles.renameInput}
              value={newTitle}
              onChangeText={setNewTitle}
              placeholder="Nouveau titre"
              placeholderTextColor={colors.slate400}
              autoFocus
              selectTextOnFocus
            />
            
            <View style={styles.renameActions}>
              <TouchableOpacity 
                style={styles.renameCancelButton}
                onPress={() => {
                  setShowRenameModal(false);
                  setNewTitle('');
                }}
                activeOpacity={0.7}
              >
                <Text style={styles.renameCancelText}>Annuler</Text>
              </TouchableOpacity>
              
              <TouchableOpacity 
                style={[styles.renameSaveButton, !newTitle.trim() && styles.renameSaveDisabled]}
                onPress={handleRename}
                disabled={!newTitle.trim() || isSaving}
                activeOpacity={0.8}
              >
                {isSaving ? (
                  <ActivityIndicator size="small" color={colors.white} />
                ) : (
                  <Text style={styles.renameSaveText}>Enregistrer</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.slate50,
  },
  headerWrapper: {
    backgroundColor: colors.slate50,
    ...(isWeb && {
      alignItems: 'center',
    }),
  },
  headerFixed: {
    paddingTop: isWeb ? 0 : 64,
    paddingBottom: isWeb ? 0 : 32,
    paddingHorizontal: 20,
    borderBottomLeftRadius: 32,
    borderBottomRightRadius: 32,
    zIndex: 10,
    width: '100%',
    maxWidth: isWeb ? MAX_WIDTH : undefined,
    ...(isWeb && {
      minHeight: 160,
      justifyContent: 'center',
      paddingBottom: 15,
    }),
  },
  headerInner: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerIconCircle: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: colors.white,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 4,
  },
  headerTextContainer: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: colors.white,
    letterSpacing: -0.5,
    marginBottom: 6,
  },
  headerSubtitle: {
    fontSize: 16,
    color: 'rgba(255,255,255,0.9)',
    fontWeight: '500',
  },
  scrollView: {
    flex: 1,
  },
  contentContainer: {
    ...(isWeb && {
      alignItems: 'center',
    }),
  },
  innerContainer: {
    width: '100%',
    maxWidth: isWeb ? MAX_WIDTH : undefined,
    alignSelf: 'center',
  },
  centered: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    color: colors.slate500,
    fontSize: 16,
  },
  section: {
    paddingHorizontal: 20,
    paddingTop: 24,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.slate900,
    letterSpacing: -0.3,
    marginBottom: 16,
  },
  sectionHint: {
    fontSize: 13,
    color: colors.slate400,
    marginBottom: 16,
  },
  actionsGrid: {
    gap: 14,
  },
  actionsRow: {
    flexDirection: 'row',
    gap: 14,
  },
  actionCard: {
    flex: 1,
    backgroundColor: colors.white,
    borderRadius: 24,
    padding: 20,
    alignItems: 'center',
    shadowColor: colors.slate900,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.06,
    shadowRadius: 12,
    elevation: 3,
    position: 'relative',
  },
  actionIconGradient: {
    width: 60,
    height: 60,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 14,
  },
  actionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.slate900,
    marginBottom: 4,
  },
  actionSubtitle: {
    fontSize: 13,
    color: colors.slate500,
  },
  actionLocked: {
    position: 'absolute',
    top: 12,
    right: 12,
    backgroundColor: colors.slate800,
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  profKylianCard: {
    borderRadius: 24,
    padding: 20,
    overflow: 'hidden',
  },
  profKylianHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  profKylianIconContainer: {
    width: 52,
    height: 52,
    borderRadius: 16,
    backgroundColor: colors.white,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
    shadowColor: colors.indigo600,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 6,
    elevation: 2,
  },
  profKylianTitleContainer: {
    flex: 1,
  },
  profKylianTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.slate900,
    marginBottom: 6,
    lineHeight: 26,
  },
  profKylianSubtitle: {
    fontSize: 14,
    color: colors.slate600,
    fontWeight: '500',
  },
  profKylianPoints: {
    marginBottom: 20,
    gap: 10,
  },
  profKylianPointRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  profKylianPointText: {
    fontSize: 14,
    color: colors.slate700,
    fontWeight: '500',
  },
  profKylianMatieres: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 12,
    marginBottom: 16,
  },
  profKylianMatiereBadge: {
    backgroundColor: colors.indigo600,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 12,
  },
  profKylianMatiereText: {
    color: colors.white,
    fontSize: 14,
    fontWeight: '700',
  },
  profKylianFreeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    marginBottom: 16,
  },
  profKylianFreeText: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.emerald500,
  },
  profKylianCTA: {
    backgroundColor: colors.white,
    paddingVertical: 16,
    borderRadius: 14,
    alignItems: 'center',
    borderWidth: 2,
    borderColor: colors.indigo600,
  },
  profKylianCTAText: {
    color: colors.indigo600,
    fontSize: 16,
    fontWeight: '700',
  },
  filtersContainer: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 16,
  },
  filterChip: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 12,
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.slate200,
  },
  filterChipActive: {
    backgroundColor: colors.indigo600,
    borderColor: colors.indigo600,
  },
  filterChipText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.slate600,
  },
  filterChipTextActive: {
    color: colors.white,
  },
  emptyCard: {
    backgroundColor: colors.white,
    borderRadius: 20,
    padding: 40,
    alignItems: 'center',
    borderWidth: 2,
    borderColor: colors.slate200,
    borderStyle: 'dashed',
  },
  emptyTitle: {
    fontSize: 17,
    fontWeight: '600',
    color: colors.slate900,
    marginTop: 16,
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 14,
    color: colors.slate500,
    textAlign: 'center',
    lineHeight: 20,
  },
  contentList: {
    gap: 12,
  },
  contentCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.white,
    borderRadius: 20,
    padding: 16,
    shadowColor: colors.slate900,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.06,
    shadowRadius: 12,
    elevation: 3,
  },
  contentIconCircle: {
    width: 52,
    height: 52,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
  },
  contentInfo: {
    flex: 1,
  },
  contentTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.slate900,
    marginBottom: 6,
  },
  contentMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  typeBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 8,
  },
  typeBadgeText: {
    fontSize: 12,
    fontWeight: '600',
  },
  contentDate: {
    fontSize: 12,
    color: colors.slate400,
  },
  scoreBadge: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 10,
    marginRight: 10,
  },
  scoreBadgeText: {
    fontSize: 13,
    fontWeight: '700',
  },
  contentChevron: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.slate100,
    alignItems: 'center',
    justifyContent: 'center',
  },
  menuOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
  },
  menuContainer: {
    backgroundColor: colors.white,
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    padding: 24,
    paddingBottom: 40,
  },
  menuHandle: {
    width: 40,
    height: 4,
    backgroundColor: colors.slate300,
    borderRadius: 2,
    alignSelf: 'center',
    marginBottom: 20,
  },
  menuTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.slate500,
    textAlign: 'center',
    marginBottom: 20,
  },
  menuOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 16,
    backgroundColor: colors.slate50,
    marginBottom: 10,
  },
  menuOptionIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: colors.indigo50,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
  },
  menuOptionText: {
    flex: 1,
    fontSize: 16,
    fontWeight: '600',
    color: colors.slate900,
  },
  menuOptionDelete: {
    color: colors.rose500,
  },
  menuCancel: {
    padding: 16,
    alignItems: 'center',
    marginTop: 8,
    backgroundColor: colors.slate100,
    borderRadius: 14,
  },
  menuCancelText: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.slate600,
  },
  renameOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  renameContainer: {
    backgroundColor: colors.white,
    borderRadius: 24,
    padding: 24,
    width: '100%',
    maxWidth: 400,
  },
  renameTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: colors.slate900,
    textAlign: 'center',
    marginBottom: 20,
  },
  renameInput: {
    backgroundColor: colors.slate50,
    borderWidth: 2,
    borderColor: colors.slate200,
    borderRadius: 14,
    padding: 16,
    fontSize: 16,
    color: colors.slate900,
    marginBottom: 20,
  },
  renameActions: {
    flexDirection: 'row',
    gap: 12,
  },
  renameCancelButton: {
    flex: 1,
    padding: 16,
    borderRadius: 14,
    backgroundColor: colors.slate100,
    alignItems: 'center',
  },
  renameCancelText: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.slate600,
  },
  renameSaveButton: {
    flex: 1,
    padding: 16,
    borderRadius: 14,
    backgroundColor: colors.indigo600,
    alignItems: 'center',
  },
  renameSaveDisabled: {
    backgroundColor: colors.slate300,
  },
  renameSaveText: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.white,
  },
});
